import { IChatMessage, IChatDetails, ISendMessage } from './../core/interfaces/chat.interface';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { computed, DestroyRef, inject } from '@angular/core';
import { ChatsApiService } from '../services/api/chatsApi/chats-api.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IChatsListItem, IChatsMessagesFilter, IDealItem } from '../core/interfaces';
import { ChatsListFilter } from '../core/classes/Filter';
import { debounceTime } from 'rxjs';
import { EChatMessageSender, EDealStatus, EPostType } from '../core/enums';
import { AuthStore } from './auth.store';
import { ToasterService } from '../services/toaster/toaster.service';

type IChatState = {
  chatsList: IChatsListItem[],
  chat: Partial<IChatDetails> | null,
  chatsListCount: number,
  isLoadingChatsList: boolean,
  isLoadingChatDetails: boolean,
  messages: IChatMessage[],
  messagesCount: number,
  isLoadingChat: boolean,
  isLoading: boolean;
  selectedPostId: number | null;
  selectedUserId: number | null;
  selectedChatId: number | null;
};

const initialState: IChatState = {
  chatsList: [],
  chat: null,
  chatsListCount: 0,
  isLoadingChatsList: false,
  isLoadingChatDetails: false,
  messages: [],
  messagesCount: 0,
  isLoading: false,
  isLoadingChat: false,
  selectedUserId: null,
  selectedPostId: null,
  selectedChatId: null,
};

const getMessageTime = (message: IChatMessage): number => new Date(message.createdAt).getTime() || 0;

const normalizeMessages = (messages: IChatMessage[]): IChatMessage[] => {
  const messagesById = new Map<number, IChatMessage>();

  messages.forEach((message) => messagesById.set(message.id, message));

  return [...messagesById.values()].sort((a, b) => {
    return getMessageTime(a) - getMessageTime(b) || a.id - b.id;
  });
};

let optimisticMessageId = -1;

export const ChatsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => {
    const authStore = inject(AuthStore);
    return {
      newMessages: computed(() => {
        return store.chatsList().reduce((prev, current) => {
          if (authStore.user()?.id !== current.lastMessageUserId) return prev + current.newMessages
          return prev
        }, 0)
      }),
      activeChatsWithNewMessages: computed(() => {
        // Only count chats where:
        // 1. There are new messages (newMessages > 0)
        // 2. The last message was NOT sent by the current user (lastMessageUserId !== current user id)
        return store.chatsList().filter((chat) => {
          return chat.newMessages > 0 && authStore.user()?.id !== chat.lastMessageUserId
        }).length;
      }),
      isPostOwner: computed(() => {
        return store.chat?.()?.post?.user?.id === authStore.user()?.id;
      }),
      isPostDeliver: computed(() => {
        return store.chat?.()?.post?.type === EPostType.deliver
      }),
      isPostTrip: computed(() => {
        return store.chat?.()?.post?.type === EPostType.trip
      }),
    }
  }),
  withComputed((store) => {
    return {
      isDealSender: computed(() => {
        return ((store.isPostDeliver() && store.isPostOwner()) || (store.isPostTrip() && !store.isPostOwner()))
      }),
    }
  }),
  withComputed((store) => {
    const authStore = inject(AuthStore);
    return {
      postSenderAndTraveler: computed(() => {
        const ME = authStore.user()?.id;
        const OTHER = store.chat()?.user?.id;
        const SENDER = store.isDealSender() ? ME : OTHER;
        const TRAVELER = store.isDealSender() ? OTHER : ME;
        return {
          sender: SENDER,
          traveler: TRAVELER,
        }
      }),
    }
  }),

  withMethods((store) => {
    return {
      setZeroChatNewMessages(chatId: number) {
        let chatIndex = store.chatsList().findIndex(item => item.id === chatId);
        let chats: IChatsListItem[] = JSON.parse(JSON.stringify(store.chatsList())) ?? [];
        let chat = chats[chatIndex];
        if (chat) {
          chat.newMessages = 0;
          patchState(store, { chatsList: chats });
        }
      }
    }
  }),
  withMethods((store) => {
    const api = inject(ChatsApiService);
    const authStore = inject(AuthStore);
    const toaster = inject(ToasterService);
    const isSelfChatItem = (chatItem: IChatsListItem) => chatItem.user?.id === authStore.user()?.id;
    const pushMessageToMessageList = (message: IChatMessage, replaceMessageId?: number) => {
      patchState(store, (state) => {
        const hasReplacedMessage = replaceMessageId !== undefined && state.messages.some(item => item.id === replaceMessageId);
        const messages = replaceMessageId !== undefined
          ? state.messages.filter(item => item.id !== replaceMessageId)
          : state.messages;
        const messageAlreadyExists = messages.some(item => item.id === message.id);

        return {
          messages: normalizeMessages([message, ...messages]),
          messagesCount: (messageAlreadyExists || hasReplacedMessage) ? state.messagesCount : state.messagesCount + 1
        };
      });
    };
    const removeMessageFromMessageList = (messageId: number) => {
      patchState(store, (state) => {
        const messageExists = state.messages.some(item => item.id === messageId);

        return {
          messages: state.messages.filter(item => item.id !== messageId),
          messagesCount: messageExists ? Math.max(0, state.messagesCount - 1) : state.messagesCount
        };
      });
    };
    const createOptimisticMessage = (data: ISendMessage): IChatMessage | null => {
      const messageValue = data instanceof FormData ? data.get('message') : null;
      const message = typeof messageValue === 'string' ? messageValue.trim() : '';

      if (!message) {
        return null;
      }

      const user = authStore.user();

      return {
        id: optimisticMessageId--,
        userId: user?.id ?? 0,
        type: EChatMessageSender.user,
        message,
        file: null,
        isRead: true,
        createdAt: new Date().toISOString(),
        user: {
          id: user?.id,
          name: user?.name ?? user?.fullName,
          fullName: user?.fullName,
          avatar: user?.avatar ?? null,
          imageUrl: user?.imageUrl ?? user?.avatarUrl ?? null,
        },
      };
    };
    const isSavedChatMessage = (message: unknown): message is IChatMessage => {
      return !!message
        && typeof message === 'object'
        && !Array.isArray(message)
        && typeof (message as IChatMessage).id === 'number'
        && typeof (message as IChatMessage).createdAt === 'string';
    };

    return {
      getChats(filter: ChatsListFilter, ref: DestroyRef, callback?: any) {
        const IS_LAST = store.chatsListCount() === store.chatsList().length && filter.page > 1;
        if (!IS_LAST) {
          patchState(store, { isLoadingChatsList: true })
          api.getChats(filter).pipe(takeUntilDestroyed(ref)).subscribe({
            next: (res) => {
              const filteredList = res.data.list.filter(item => !isSelfChatItem(item));
              // If it's page 1, replace the list. Otherwise, append.
              const newChatsList = filter.page === 1 ? filteredList : [...store.chatsList(), ...filteredList];
              patchState(store, { chatsList: newChatsList, chatsListCount: res.data.totalRecords })
            },
            complete: () => patchState(store, { isLoadingChatsList: false }),
            error: () => patchState(store, { isLoadingChatsList: false })
          });
        }
      },
      getChatDetails(userId: number, ref: DestroyRef, postId?: number, callback?: any) {
        if (userId && userId === authStore.user()?.id) {
          toaster.addWarning('You cannot open a chat with yourself.');
          patchState(store, { isLoadingChatDetails: false, chat: null, selectedChatId: null });
          return;
        }

        patchState(store, { isLoadingChatDetails: true, messages: [], messagesCount: 0 })
        api.getChatDetails(userId, postId).pipe(takeUntilDestroyed(ref)).subscribe({
          next: (res) => {
            patchState(store, { chat: res.data, selectedChatId: res.data?.chat?.id })
            store.setZeroChatNewMessages(res.data?.chat?.id)
            callback?.();
          },
          complete: () => patchState(store, { isLoadingChatDetails: false }),
          error: () => patchState(store, { isLoadingChatDetails: false })
        });
      },
      getMessages(filter: IChatsMessagesFilter, chatId: number, ref: DestroyRef, callback?: any) {
        const IS_LAST = store.messagesCount() === store.messages().length && filter.page > 1;
        if (!IS_LAST) {
          patchState(store, { isLoadingChat: true })
          api.getChatMessages(filter, chatId).pipe(
            debounceTime(1000),
            takeUntilDestroyed(ref)).subscribe({
              next: (res) => {
                patchState(store, (state) => ({
                  messages: normalizeMessages([...state.messages, ...res.data.list]),
                  messagesCount: res.data.totalRecords
                }));
                callback?.()
              },
              complete: () => patchState(store, { isLoadingChat: false }),
              error: () => patchState(store, { isLoadingChat: false })
            });
        }
      },
      sendMessage(data: ISendMessage, chatId: number, ref: DestroyRef, callback?: any) {
        const optimisticMessage = createOptimisticMessage(data);

        if (optimisticMessage) {
          pushMessageToMessageList(optimisticMessage);
        }

        api.sendMessage(data, chatId).pipe(takeUntilDestroyed(ref)).subscribe({
          next: (res) => {
            if (isSavedChatMessage(res.data)) {
              pushMessageToMessageList(res.data, optimisticMessage?.id);
            }
            callback?.(isSavedChatMessage(res.data) ? res.data : optimisticMessage)
          },
          error: () => {
            if (optimisticMessage) {
              removeMessageFromMessageList(optimisticMessage.id);
            }
          },
        });
      },
      setSelectedUserAndPostIds(userId: number, postId?: number) {
        if (userId && userId === authStore.user()?.id) {
          toaster.addWarning('You cannot open a chat with yourself.');
          return;
        }

        patchState(store, { selectedUserId: userId, selectedPostId: postId })
      },
      resetSelectedUserAndPostIds() {
        patchState(store, { selectedUserId: undefined, selectedPostId: undefined })
      },
      resetMessages() {
        patchState(store, { messages: [] });
      },
      resetChatsList() {
        patchState(store, { chatsList: [], chatsListCount: 0 });
      },
      resetStore() {
        patchState(store, { ...initialState });
      },
      pushMessageToMessageList(message: IChatMessage) {
        pushMessageToMessageList(message);
      },
      checkAndPushNewChatListItem(chatListItem: IChatsListItem) {
        if (isSelfChatItem(chatListItem)) {
          return;
        }

        let chats: IChatsListItem[] = JSON.parse(JSON.stringify(store.chatsList())) ?? [];
        let firstItem = chats[0];
        let { lastMessage, lastTimeMessage, newMessages } = chatListItem;
        const isCurrentUserLastSender = authStore.user()?.id === chatListItem.lastMessageUserId;
        const isCurrentChatOpen = store.selectedChatId() === chatListItem.id;
        if (isCurrentUserLastSender || isCurrentChatOpen) {
          newMessages = 0
        }
        if (chatListItem.id === firstItem?.id) {
          chats[0] = { ...firstItem, ...chatListItem, lastMessage, lastTimeMessage, newMessages };
        }
        else {
          const index = chats.findIndex(item => item.id === chatListItem.id);
          let item!: IChatsListItem;
          if (index !== -1) {
            item = chats[index];
            chats.splice(index, 1);
          } else {
            item = chatListItem;
          }
          chats = [{ ...item, ...chatListItem, lastMessage, lastTimeMessage, newMessages }, ...chats];
        }
        patchState(store, { chatsList: chats });
      },
      updateChatDeal(deal: IDealItem) {
        patchState(store, state => ({
          ...state,
          chat: {
            ...state.chat,
            deal: deal
          }
        }
        ))
      },
      updateChatDealStatus(status: EDealStatus, dealId?: number) {
        patchState(store, state => {
          if (!state.chat) {
            return state;
          }

          const currentDeal = state.chat.deal;
          const nextDeal = dealId && currentDeal?.id !== dealId
            ? ({ id: dealId, status } as IDealItem)
            : currentDeal
              ? ({ ...currentDeal, ...(dealId ? { id: dealId } : {}), status } as IDealItem)
              : dealId
                ? ({ id: dealId, status } as IDealItem)
                : null;

          if (!nextDeal) {
            return state;
          }

          return {
            ...state,
            chat: {
              ...state.chat,
              deal: nextDeal
            }
          };
        });
      },
    }
  })
);
